import { defaultRoundFor } from '@fp/core/time';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Badge } from '../components/badges';
import { EntityPicker, refOf, type NewCompetition, type NewTeam, type Picked } from '../components/EntityPicker';
import { Fixture, KickoffCell, MatchFlags, StatusPill } from '../components/match';
import { Topbar } from '../components/Shell';
import { Empty, ErrorBox, errorText, Field, Loading, Seg, useToast } from '../components/ui';
import { ApiError, get, post, qs } from '../lib/api';
import { fmtDateTime, fromLocalInput, STATUS_LABEL, toLocalInput } from '../lib/format';
import type { Competition, GameSettings, MatchRow, MatchStatus, Round } from '../lib/types';

type Filters = { q?: string; status?: string; competitionId?: number; roundId?: number; published?: string };

export function MatchesPage() {
  const search = useSearch({ strict: false }) as Filters;
  const navigate = useNavigate();
  const set = (patch: Filters) => navigate({ to: '/matches', search: { ...search, ...patch } });
  const comps = useQuery({ queryKey: ['competitions'], queryFn: () => get<{ competitions: Competition[] }>('/admin/competitions') });
  const rounds = useQuery({ queryKey: ['rounds', 'all'], queryFn: () => get<{ rounds: Round[] }>('/admin/rounds') });
  const q = useQuery({
    queryKey: ['matches', search],
    queryFn: () => get<{ matches: MatchRow[] }>(`/admin/matches${qs({ ...search, limit: 300 })}`),
    refetchInterval: 20_000,
  });
  const [text, setText] = useState(search.q ?? '');
  return (
    <>
      <Topbar
        title="Matches"
        sub="Every stored match: create, schedule, run live, correct and void"
        actions={
          <>
            <Link to="/matches/import" className="btn btn-primary">
              Import from the data feed
            </Link>
            <Link to="/matches/new" className="btn">
              New match by hand
            </Link>
          </>
        }
      />
      <div className="page">
        <div className="card">
          <form
            className="card-head"
            onSubmit={(e) => {
              e.preventDefault();
              set({ q: text || undefined });
            }}
          >
            <input className="input" style={{ width: 240 }} placeholder="Search teams or competitions" aria-label="Search" value={text} onChange={(e) => setText(e.target.value)} />
            <select className="select" aria-label="Status" value={search.status ?? ''} onChange={(e) => set({ status: e.target.value || undefined })}>
              <option value="">Any status</option>
              <option value="LIVE">Live now</option>
              {(Object.keys(STATUS_LABEL) as MatchStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <select className="select" aria-label="Competition" value={search.competitionId ?? ''} onChange={(e) => set({ competitionId: e.target.value ? Number(e.target.value) : undefined })}>
              <option value="">All competitions</option>
              {comps.data?.competitions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.season}
                </option>
              ))}
            </select>
            <select className="select" aria-label="Round" value={search.roundId ?? ''} onChange={(e) => set({ roundId: e.target.value ? Number(e.target.value) : undefined })}>
              <option value="">All rounds</option>
              {rounds.data?.rounds.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.season})
                </option>
              ))}
            </select>
            <select className="select" aria-label="Visibility" value={search.published ?? ''} onChange={(e) => set({ published: e.target.value || undefined })}>
              <option value="">Published and drafts</option>
              <option value="true">Published</option>
              <option value="false">Drafts</option>
            </select>
            <button className="btn">Search</button>
          </form>
          {q.error && <ErrorBox error={q.error} />}
          {q.isLoading ? (
            <Loading />
          ) : q.data?.matches.length === 0 ? (
            <Empty>No matches match these filters.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kickoff</th>
                    <th>Round</th>
                    <th>Competition</th>
                    <th>Match</th>
                    <th>Status</th>
                    <th className="right">Picks</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.data?.matches.map((m) => (
                    <tr key={m.id} className="clickable" onClick={() => navigate({ to: '/matches/$matchId', params: { matchId: String(m.id) } })}>
                      <td>
                        <KickoffCell m={m} />
                      </td>
                      <td className="muted">{m.roundName ?? '—'}</td>
                      <td>
                        <span className="row" style={{ flexWrap: 'nowrap' }}>
                          <Badge spec={m.competitionBadge} size={18} />
                          {m.competitionName}
                        </span>
                      </td>
                      <td style={{ minWidth: 300 }}>
                        <Fixture m={m} link={false} />
                      </td>
                      <td>
                        <StatusPill m={m} />
                      </td>
                      <td className="right num">{m.predictionCount.toLocaleString()}</td>
                      <td className="right">
                        <MatchFlags m={m} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function nextSaturday3pm(): string {
  const d = new Date();
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7));
  d.setHours(15, 0, 0, 0);
  return toLocalInput(d.toISOString());
}

/**
 * New match. Competition and teams are typeahead pickers: pick an existing one,
 * or type a new name and it is created with the match (badge generated from its
 * colours, editable here). The round defaults to the kickoff's week ("auto").
 */
export function MatchNewPage() {
  const search = useSearch({ strict: false }) as { roundId?: number };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<{ settings: GameSettings }>('/admin/settings') });
  const season = settings.data?.settings.currentSeason;
  const rounds = useQuery({ queryKey: ['rounds', season], queryFn: () => get<{ rounds: Round[] }>(`/admin/rounds${qs({ season })}`), enabled: !!season });

  const [competition, setCompetition] = useState<Picked<NewCompetition>>(null);
  const [home, setHome] = useState<Picked<NewTeam>>(null);
  const [away, setAway] = useState<Picked<NewTeam>>(null);
  const [kickoff, setKickoff] = useState(nextSaturday3pm());
  const [confirmed, setConfirmed] = useState(true);
  const [roundMode, setRoundMode] = useState<'auto' | 'existing' | 'new'>(search.roundId ? 'existing' : 'auto');
  const [roundId, setRoundId] = useState<number | undefined>(search.roundId);
  const [newRound, setNewRound] = useState({ name: '', startsAt: '', endsAt: '' });
  const [published, setPublished] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<number | null>(null);

  const kickoffIso = fromLocalInput(kickoff);
  const auto = useMemo(() => (kickoffIso ? defaultRoundFor(new Date(kickoffIso)) : null), [kickoffIso]);
  const autoExisting = rounds.data?.rounds.find((r) => r.name === auto?.name);
  const willCreate = [
    competition?.kind === 'new' && `competition “${competition.create.name}”`,
    home?.kind === 'new' && `team “${home.create.name}”`,
    away?.kind === 'new' && `team “${away.create.name}”`,
    ((roundMode === 'auto' && auto && !autoExisting) || roundMode === 'new') && `round “${roundMode === 'auto' ? auto?.name : newRound.name || 'new round'}”`,
  ].filter(Boolean) as string[];

  const nameOf = (p: Picked<NewTeam> | Picked<NewCompetition>) => (p ? (p.kind === 'existing' ? p.name : p.create.name) : null);
  const sameTeam = home && away && (nameOf(home) ?? '').trim().toLowerCase() === (nameOf(away) ?? '').trim().toLowerCase();

  async function submit(allowDuplicate = false) {
    setError(null);
    if (!competition || !home || !away || !kickoffIso) {
      setError('Choose the competition, both teams and the kickoff');
      return;
    }
    setBusy(true);
    try {
      const round =
        roundMode === 'auto'
          ? 'auto'
          : roundMode === 'existing'
            ? roundId
              ? { id: roundId }
              : null
            : { create: { name: newRound.name, season, startsAt: fromLocalInput(newRound.startsAt), endsAt: fromLocalInput(newRound.endsAt) } };
      const r = await post<{ matchId: number; created: Record<string, boolean> }>('/admin/matches', {
        competition: refOf(competition),
        home: refOf(home),
        away: refOf(away),
        round,
        kickoffAt: kickoffIso,
        kickoffConfirmed: confirmed,
        published: published && round !== null,
        allowDuplicate,
      });
      const made = Object.entries(r.created).filter(([, v]) => v).map(([k]) => k);
      toast(made.length ? `Match created, along with the new ${made.join(', ')}` : 'Match created');
      await qc.invalidateQueries();
      navigate({ to: '/matches/$matchId', params: { matchId: String(r.matchId) } });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'DUPLICATE_MATCH') {
        setDuplicateOf((e.details as { matchId?: number } | undefined)?.matchId ?? null);
        setError('This fixture already exists within a day of that kickoff.');
      } else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Topbar title="New match" sub="Teams, competition and round that don't exist yet are created with it" />
      <div className="page">
        <div className="cols cols-main">
          <form
            className="card"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <div className="card-body stack" style={{ gap: 18 }}>
              <EntityPicker kind="competition" label="Competition" value={competition} onChange={setCompetition} season={season} />
              <div className="cols cols-2" style={{ gap: 16 }}>
                <EntityPicker kind="team" label="Home team" value={home} onChange={setHome} exclude={away?.kind === 'existing' ? away.id : undefined} />
                <EntityPicker kind="team" label="Away team" value={away} onChange={setAway} exclude={home?.kind === 'existing' ? home.id : undefined} />
              </div>
              {sameTeam && <div className="err-box">Home and away must be different teams.</div>}
              <div className="form-grid">
                <Field label="Kickoff" hint="Your local time">
                  <input className="input" type="datetime-local" value={kickoff} onChange={(e) => setKickoff(e.target.value)} required />
                </Field>
                <label className="check" style={{ alignSelf: 'end' }}>
                  <input type="checkbox" checked={!confirmed} onChange={(e) => setConfirmed(!e.target.checked)} />
                  Time to be confirmed (keeps predictions closed)
                </label>
              </div>
              <div className="stack" style={{ gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>Round</span>
                <Seg
                  label="Round"
                  value={roundMode}
                  onChange={setRoundMode}
                  options={[
                    ['auto', auto ? `${auto.name} (${autoExisting ? 'existing' : 'new'})` : 'Kickoff week'],
                    ['existing', 'Pick a round'],
                    ['new', 'New round'],
                  ]}
                />
                {roundMode === 'existing' && (
                  <select className="select" aria-label="Round" value={roundId ?? ''} onChange={(e) => setRoundId(e.target.value ? Number(e.target.value) : undefined)}>
                    <option value="">No round (a draft that can't be published)</option>
                    {rounds.data?.rounds.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} · {r.publishedCount}/{r.matchCount} published
                      </option>
                    ))}
                  </select>
                )}
                {roundMode === 'new' && (
                  <div className="form-grid">
                    <Field label="Name">
                      <input className="input" value={newRound.name} onChange={(e) => setNewRound({ ...newRound, name: e.target.value })} placeholder="Week 43" />
                    </Field>
                    <Field label="Starts">
                      <input className="input" type="datetime-local" value={newRound.startsAt} onChange={(e) => setNewRound({ ...newRound, startsAt: e.target.value })} />
                    </Field>
                    <Field label="Ends">
                      <input className="input" type="datetime-local" value={newRound.endsAt} onChange={(e) => setNewRound({ ...newRound, endsAt: e.target.value })} />
                    </Field>
                  </div>
                )}
              </div>
              <label className="check">
                <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
                Publish now (players can see and predict it)
              </label>
              {error && (
                <div className="err-box row">
                  <span className="grow">{error}</span>
                  {duplicateOf && (
                    <>
                      <Link to="/matches/$matchId" params={{ matchId: String(duplicateOf) }} className="btn btn-sm">
                        Open it
                      </Link>
                      <button type="button" className="btn btn-sm" onClick={() => void submit(true)}>
                        Create anyway
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="card-foot row">
              <span className="grow">{willCreate.length ? `Also creates: ${willCreate.join(', ')}.` : 'Uses existing records only.'}</span>
              <button type="button" className="btn" onClick={() => history.back()}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={busy || !!sameTeam}>
                {busy ? 'Creating…' : 'Create match'}
              </button>
            </div>
          </form>

          <aside className="card" aria-label="Preview">
            <div className="card-head">
              <h2>Preview</h2>
            </div>
            <div className="card-body stack" style={{ alignItems: 'center', textAlign: 'center', gap: 14 }}>
              {competition && (
                <span className="row muted" style={{ justifyContent: 'center' }}>
                  <Badge spec={competition.kind === 'existing' ? competition.badge : competition.create.badge} size={22} />
                  {nameOf(competition)}
                </span>
              )}
              <div className="scoreboard" style={{ width: '100%' }}>
                <div className="side">
                  {home ? <Badge spec={home.kind === 'existing' ? home.badge : home.create.badge} size={72} /> : <Placeholder />}
                  <span>{nameOf(home) ?? 'Home'}</span>
                </div>
                <span className="display muted" style={{ fontSize: 28 }}>
                  v
                </span>
                <div className="side">
                  {away ? <Badge spec={away.kind === 'existing' ? away.badge : away.create.badge} size={72} /> : <Placeholder />}
                  <span>{nameOf(away) ?? 'Away'}</span>
                </div>
              </div>
              <span className="muted">
                {kickoffIso ? fmtDateTime(kickoffIso) : 'Pick a kickoff'}
                {!confirmed && ' · time TBC'}
              </span>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

function Placeholder() {
  return <span style={{ width: 72, height: 86, borderRadius: 12, border: '2px dashed var(--line)', display: 'block' }} aria-hidden="true" />;
}
